/**
 * Delayed Order Payment Form
 * Feature: 003-jag-vill-justera
 * Purpose: Payment form for delayed order creation flow
 *
 * IMPORTANT: PaymentIntent is created when component mounts, then Elements
 * uses that clientSecret for manual capture authorization.
 */

import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe } from '@/services/stripe/stripeClient';
import { PaymentError, getPaymentErrorMessage } from '@/services/stripe/errorCodes';

export interface DelayedOrderPaymentFormProps {
  cartItems: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    producerId: string;
  }>;
  customerEmail: string;
  deliveryDate: string;
  deliveryTime: string;
  guestDetails: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    address: string;
  };
  deliveryAddress: string;
  deliveryNotes: string;
  onSuccess: (paymentIntentId: string) => void; // T012: Now passes paymentIntent.id directly
  onError: (error: string) => void;
}

/**
 * Inner form component that uses Stripe Elements
 *
 * This component shows the Stripe payment form WITHOUT creating a PaymentIntent first.
 * The PaymentIntent is created when the user submits the form.
 */
function PaymentFormInner({
  cartItems,
  customerEmail,
  deliveryDate,
  deliveryTime,
  onSuccess,
  onError,
}: DelayedOrderPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Step 1: Submit the payment form to Stripe to validate card details
      const { error: submitError } = await elements.submit();

      if (submitError) {
        setErrorMessage(submitError.message || 'Kunde inte verifiera kortuppgifter');
        setIsProcessing(false);
        return;
      }

      // Step 2: Confirm the PaymentIntent that was created when component mounted
      // Elements already has the clientSecret from initialization
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/order-confirmation`,
        },
      });

      // T015: User-friendly error messages for payment failures
      if (confirmError) {
        let friendlyMessage = 'Betalningen misslyckades. ';

        // Map common Stripe error codes to user-friendly messages
        if (confirmError.code === 'card_declined') {
          friendlyMessage += 'Ditt kort avvisades. Vänligen försök med ett annat kort eller kontakta din bank.';
        } else if (confirmError.code === 'insufficient_funds') {
          friendlyMessage += 'Otillräckliga medel på kortet. Vänligen försök med ett annat kort.';
        } else if (confirmError.code === 'expired_card') {
          friendlyMessage += 'Kortet har gått ut. Vänligen använd ett giltigt kort.';
        } else if (confirmError.code === 'incorrect_cvc') {
          friendlyMessage += 'Felaktig CVC-kod. Vänligen kontrollera och försök igen.';
        } else {
          friendlyMessage += confirmError.message || 'Ett oväntat fel uppstod. Vänligen försök igen.';
        }

        setErrorMessage(friendlyMessage);
        setIsProcessing(false);
        return;
      }

      if (!paymentIntent) {
        setErrorMessage('Betalningsauktorisering misslyckades. Vänligen försök igen eller kontakta kundtjänst.');
        setIsProcessing(false);
        return;
      }

      // Check payment status
      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
        // Payment authorized! (requires_capture means authorization successful, waiting for capture)
        // Webhook will create the order automatically
        // T012: Pass paymentIntent.id directly (not as array) - parent will poll for order
        onSuccess(paymentIntent.id);
      } else if (paymentIntent.status === 'requires_action') {
        // 3D Secure or other authentication needed
        setErrorMessage('Ytterligare autentisering krävs. Följ instruktionerna.');
        setIsProcessing(false);
      } else {
        setErrorMessage(`Betalningsstatus: ${paymentIntent.status}`);
        setIsProcessing(false);
      }
    } catch (error) {
      logger.error('Payment error:', error);
      let errorMsg = 'Ett fel uppstod vid betalningen';

      if (error instanceof PaymentError) {
        errorMsg = getPaymentErrorMessage(error.code);
      } else if (error instanceof Error) {
        errorMsg = error.message;
      }

      setErrorMessage(errorMsg);
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{errorMessage}</div>
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Leveransdatum:</span>
          <span className="font-medium">{deliveryDate}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Leveranstid:</span>
          <span className="font-medium">{deliveryTime}</span>
        </div>
      </div>

      <div className="space-y-4">
        <PaymentElement
          options={{
            wallets: {
              applePay: 'auto',
              googlePay: 'auto'
            }
          }}
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Viktig information om betalning
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Vi reserverar hela ordervärdet + 10% då vissa varor är viktvaror</li>
                <li>Betalning dras när ordern är packad</li>
                <li>Slutgiltig summa kan justeras baserat på faktisk vikt</li>
                <li>Reservationen är aktiv i 7 dagar</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
          !stripe || isProcessing
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
        }`}
      >
        {isProcessing ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Behandlar betalning...
          </>
        ) : (
          'Betala och slutför beställning'
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        Genom att klicka på "Betala och slutför beställning" godkänner du våra villkor
        och bekräftar att betalningsinformationen är korrekt.
      </p>
    </form>
  );
}

/**
 * Main wrapper component
 *
 * This component creates PaymentIntent FIRST, then renders Elements with clientSecret.
 * This is required for manual capture to work correctly.
 */
export const DelayedOrderPaymentForm: React.FC<DelayedOrderPaymentFormProps> = (props) => {
  const stripePromise = getStripe();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Create PaymentIntent when component mounts
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const orderData = {
          delivery_address: props.deliveryAddress,
          customer_id: null,
          delivery_date: props.deliveryDate,
          delivery_notes: props.deliveryNotes,
          delivery_time: props.deliveryTime,
          guest_details: props.guestDetails,
          items: props.cartItems.map(item => ({
            product_id: item.id,
            quantity: item.quantity,
            unit_price: item.price,
          })),
          total_amount: props.cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        };

        const response = await fetch('/.netlify/functions/authorize-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderData,
            customerEmail: props.customerEmail,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Kunde inte skapa betalning');
        }

        const responseData = await response.json();
        setClientSecret(responseData.clientSecret);
      } catch (err) {
        logger.error('Error creating PaymentIntent:', err);
        setError(err instanceof Error ? err.message : 'Ett fel uppstod');
      }
    };

    createPaymentIntent();
  }, []); // Run once on mount

  // Show loading state while creating PaymentIntent
  if (!clientSecret && !error) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Show error if PaymentIntent creation failed
  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-700">{error}</div>
      </div>
    );
  }

  // Stripe Elements options - use clientSecret from PaymentIntent
  // Feature 007: Enable Apple Pay and Google Pay wallets
  const elementsOptions: StripeElementsOptions = {
    clientSecret: clientSecret!,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#16a34a', // green-600
        colorBackground: '#ffffff',
        colorText: '#111827', // gray-900
        colorDanger: '#dc2626', // red-600
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '6px',
      },
    },
    locale: 'sv',
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentFormInner {...props} />
    </Elements>
  );
};
